import { inject, Injectable } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationError,
  Router,
} from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';
import { SimXUser } from '../../../shared/models/entities/simx-user.model';
import { AuthenticationService } from '../../authentication/services/authentication.service';
import * as AuthenticationStoreActions from '../../authentication/store/authentication-store.actions';
import { AuthorizationService } from '../../authorization/services/authorization.service';
import { primaryNavigationItems } from '../definitions/primary-navigation-items.const';
import { userNavigationItems } from '../definitions/user-navigation-items.const';
import { AppNavigationItem } from '../models/app-navigation-item.model';

@Injectable({ providedIn: 'root' })
export class AppNavigationService {
  private readonly _actions$ = inject(Actions);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _authorizationService = inject(AuthorizationService);
  private readonly _router = inject(Router);

  authenticatedUser: SimXUser = null;

  showAppHeader: BehaviorSubject<boolean> = new BehaviorSubject(false);
  showAppHeader$: Observable<boolean> = this.showAppHeader.asObservable();
  showAppSidebar: BehaviorSubject<boolean> = new BehaviorSubject(false);
  showAppSidebar$: Observable<boolean> = this.showAppSidebar.asObservable();

  primaryNavigationItems: BehaviorSubject<Array<AppNavigationItem>> =
    new BehaviorSubject([]);
  primaryNavigationItems$: Observable<Array<AppNavigationItem>> =
    this.primaryNavigationItems.asObservable();
  userNavigationItems: BehaviorSubject<Array<AppNavigationItem>> =
    new BehaviorSubject([]);
  userNavigationItems$: Observable<Array<AppNavigationItem>> =
    this.userNavigationItems.asObservable();

  appViewTitle: BehaviorSubject<string> = new BehaviorSubject(null);
  appViewTitle$: Observable<string> = this.appViewTitle.asObservable();

  constructor() {
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map((event: NavigationEnd) => {
          return this._activatedRoute;
        }),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap(route => route.data),
      )
      .subscribe(routeData => {
        this.refreshAppViewTitle(routeData.title || null);
        this.refreshShowAppHeader(
          routeData.hasOwnProperty('showAppHeader')
            ? routeData.showAppHeader
            : true,
        );
        this.refreshShowAppSidebar(
          routeData.hasOwnProperty('showAppSidebar')
            ? routeData.showAppSidebar
            : true,
        );

        this._actions$
          .pipe(ofType(AuthenticationStoreActions.loginSucceeded))
          .subscribe(() => {
            this.refreshAllowedPrimaryNavigationItems();
            this.refreshAllowedUserNavigationItems();
          });
      });

    this._router.events
      .pipe(filter(event => event instanceof NavigationError))
      .subscribe((_: NavigationError) => {
        this._router.navigate(['']);
      });

    this._authenticationService.getUser$().subscribe((user: SimXUser) => {
      this.authenticatedUser = user;
      this.refreshAllowedPrimaryNavigationItems();
      this.refreshAllowedUserNavigationItems();
    });
  }

  private refreshShowAppHeader(state: boolean): void {
    this.showAppHeader.next(state);
  }

  private refreshShowAppSidebar(state: boolean): void {
    this.showAppSidebar.next(state);
  }

  private async refreshAllowedPrimaryNavigationItems(): Promise<void> {
    this.primaryNavigationItems.next(
      await this.getAllowedNavigationItems(primaryNavigationItems),
    );
  }

  private async refreshAllowedUserNavigationItems(): Promise<void> {
    const isAuthenticated = await firstValueFrom(
      this._authenticationService.getIsAuthenticated$(),
    );
    const navigationItems = isAuthenticated ? userNavigationItems : [];
    this.userNavigationItems.next(
      await this.getAllowedNavigationItems(navigationItems),
    );
  }

  private refreshAppViewTitle(title: string): void {
    const currentTitle = this.appViewTitle.getValue();
    if (title !== currentTitle) {
      this.appViewTitle.next(title);
    }
  }

  private async getAllowedNavigationItems(
    navigationItemSet: Array<AppNavigationItem>,
  ): Promise<Array<AppNavigationItem>> {
    const items: Array<AppNavigationItem> = [];

    for (const item of navigationItemSet) {
      if (!item.permissions.length) {
        items.push(item);
      } else {
        for (const permission of item.permissions) {
          if (
            await firstValueFrom(
              this._authorizationService.authenticatedUserCan(permission),
            )
          ) {
            items.push(item);
            break;
          }
        }
      }
    }

    return items;
  }
}
